import { isEmpty } from 'lodash-es';

import { isNullOrUndefined } from '@app/util/util';
import {
  AutoDirections, AutoPlaceParams, AutoURLParams
} from '@autos/auto-results/entities/autoURLParams';
import { AutoDateValidator } from '@autos/auto-results/entities/date-validator';
import { formatDateTime } from '@autos/auto-results/helpers/auto.helpers';
import {
  DropOffSearch, PickUpSearch, VehiclePlaceType, VehicleSearchPayload
} from '@autos/entities/vehicle-search.payload';
import { environment } from '@environment';
import { SelectAutocompletePrediction } from '@search-bar/entities/select-autocomplete-prediction';
import { getBaseRequestSettings } from '@app/mappers/reguest-settings.mapper';

const addStartDate = 15;
const addEndDate = 20;

type Payload = Readonly<{
  pick_up_place: string;
  pick_up_location?: string;
  drop_off_place: string;
  drop_off_location?: string;
  pick_up_date: string;
  drop_off_date: string;
  currency_code: string;
}>;

export function mapPayloadAuto(payload: Payload, isMobile: boolean): VehicleSearchPayload {
  let pickUpType = VehiclePlaceType.City;
  let pickUpPlace = payload.pick_up_place;

  if (pickUpPlace && pickUpPlace.indexOf('airport:') > -1) {
    pickUpType = VehiclePlaceType.Airport;
    pickUpPlace = pickUpPlace.replace('airport:', '');
  }

  let pickUp: PickUpSearch = {
    PickUpType: pickUpType,
    PickUpDateTime: payload.pick_up_date,
    DestinationCode: pickUpPlace
  };

  if (pickUpPlace && pickUpPlace.indexOf(',') > -1) {
    const pickUpLocation = pickUpPlace.split(',');
    pickUp = {
      PickUpType: VehiclePlaceType.LatLong,
      PickUpDateTime: payload.pick_up_date,
      Latitude: Number(pickUpLocation[1]),
      Longitude: Number(pickUpLocation[2])
    };
  }

  let dropOffType = VehiclePlaceType.City;
  let dropOffPlace = payload.drop_off_place;
  if (dropOffPlace && dropOffPlace.indexOf('airport:') > -1) {
    dropOffType = VehiclePlaceType.Airport;
    dropOffPlace = dropOffPlace.replace('airport:', '');
  }

  let dropOff: DropOffSearch = {
    DropOffType: dropOffType,
    DropOffDateTime: payload.drop_off_date,
    DestinationCode: dropOffPlace
  };

  if (dropOffPlace && dropOffPlace.indexOf(',') > -1) {
    const dropOffLocation = dropOffPlace.split(',');
    dropOff = {
      DropOffType: VehiclePlaceType.LatLong,
      DropOffDateTime: payload.drop_off_date,
      Latitude: Number(dropOffLocation[1]),
      Longitude: Number(dropOffLocation[2])
    };
  }

  return {
    PickUp: pickUp,
    DropOff: dropOff,
    MaxResults: 1000,
    CurrencyCode: payload.currency_code,
    RequestSettings: getBaseRequestSettings(isMobile)
  };
}

export function mapSearchAutoPayload(payload: VehicleSearchPayload, token: string): VehicleSearchPayload {
  return {
    ...payload,
    SessionToken: token,
    CurrencyCode: environment.countryInfo.currency_code
  };
}

export function dateAutoValidation(datePickUp: Date, dateDropOff: Date): AutoDateValidator {
  const currentDatePickUp = new Date();
  const currentDateDropOff = new Date();

  if (dateDropOff < currentDateDropOff || datePickUp < currentDatePickUp) {
    currentDatePickUp.setDate(currentDatePickUp.getDate() + addStartDate);
    currentDateDropOff.setDate(currentDateDropOff.getDate() + addEndDate);
    return {
      dateDropOff: currentDateDropOff,
      datePickUp: currentDatePickUp,
      valid: false
    };
  } else return { datePickUp, dateDropOff, valid: true };
}

export function mapVehiclePayload(
  searchParams: VehicleSearchPayload,
  placePickUp?: SelectAutocompletePrediction | AutoPlaceParams,
  placeDropOff?: SelectAutocompletePrediction | AutoPlaceParams,
  category?: string
): AutoURLParams {
  const dateValidation = dateAutoValidation(new Date(searchParams.PickUp.PickUpDateTime),
    new Date(searchParams.DropOff.DropOffDateTime));
  const mapped: AutoURLParams = {
    departureDate: formatDateTime(dateValidation.datePickUp),
    departurePlaceCode: (placePickUp && placePickUp.placeId) ?
      `${placePickUp.placeId},${placePickUp.lat},${placePickUp.lng}` : searchParams.PickUp.DestinationCode,
    departureIsAirport: searchParams.PickUp.PickUpType === VehiclePlaceType.Airport ? true : false,
    arrivalDate: formatDateTime(dateValidation.dateDropOff),
    arrivalPlaceCode: (placeDropOff && placeDropOff.placeId) ?
      `${placeDropOff.placeId},${placeDropOff.lat},${placeDropOff.lng}` : searchParams.DropOff.DestinationCode,
    arrivalIsAirport: searchParams.DropOff.DropOffType === VehiclePlaceType.Airport ? true : false,
    currencyCode: `${environment.countryInfo.currency_code}`
  };
  if (placePickUp) {
    mapped.pickUp = {
      ...placePickUp,
      lat: placePickUp.lat ? placePickUp.lat : 0,
      lng: placePickUp.lng ? placePickUp.lng : 0,
      placeId: placePickUp.placeId ? placePickUp.placeId : ''
    };
  }
  if (placeDropOff) {
    mapped.dropOff = {
      ...placeDropOff,
      lat: placeDropOff.lat ? placeDropOff.lat : 0,
      lng: placeDropOff.lng ? placeDropOff.lng : 0,
      placeId: placeDropOff.placeId ? placeDropOff.placeId : ''
    };
  }
  if (category && category !== '') {
    mapped.category = category;
  }
  return mapped;
}

export function formatAutoDirections(urlParams: AutoURLParams): AutoDirections {
  const pickUp = urlParams.pickUp;
  const dropOff = urlParams.dropOff;
  let origin = '';


  if (pickUp && !isNullOrUndefined(pickUp.lat) && !isNullOrUndefined(pickUp.lng)) {
    origin = `${pickUp.placeId},${pickUp.lat},${pickUp.lng}`;
  }
  
  if (!(isNullOrUndefined(urlParams.departurePlaceCode) ||
    isEmpty(urlParams.departurePlaceCode) ||
    urlParams.departurePlaceCode === '')) {
    origin = urlParams.departurePlaceCode;
    const departureIsAirport = urlParams.departureIsAirport ? 'airport:' : '';
    origin = `${departureIsAirport}${origin}`;
  }

  let destination = origin;
  if (!(isNullOrUndefined(urlParams.arrivalPlaceCode) ||
    isEmpty(urlParams.arrivalPlaceCode) ||
    urlParams.arrivalPlaceCode === '')) {
    const arrivalIsAirport = urlParams.arrivalIsAirport ? 'airport:' : '';
    destination = `${arrivalIsAirport}${urlParams.arrivalPlaceCode}`;
  }
  if (dropOff && !isNullOrUndefined(dropOff.lat) && !isNullOrUndefined(dropOff.lng)) {
    destination = `${dropOff.placeId},${dropOff.lat},${dropOff.lng}`;
  }
  return { origin, destination };
}
